import { useEffect, useState } from 'react'
import { listUsers, listRoleOptions, deleteUser, type UserRow } from '../api'
import { useTabs, useDataVersion } from '../layout/TabsContext'
import { useAuth } from '../layout/AuthContext'
import { useToast } from '../layout/ToastContext'
import { useServerList } from '../hooks/useServerList'
import { useColumnVisibility } from '../hooks/useColumnVisibility'
import ListToolbar from '../components/ListToolbar'
import ListPanel from '../components/ListPanel'
import Pagination from '../components/Pagination'
import DataTable, { type Column } from '../components/DataTable'
import ColumnManager from '../components/ColumnManager'
import ConfirmDialog from '../components/ConfirmDialog'
import { useBulkTrash } from '../components/BulkTrash'
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/constants'
import { fill, PAGES_USERS_LIST_TEXTS as T } from '../../shared/texts'

const EditIcon = () => <img src="/icons/edit.svg" alt="" aria-hidden="true" />
const TrashIcon = () => <img src="/icons/delete.svg" alt="" aria-hidden="true" />

export default function UsersList() {
    const { openTab, publishChange } = useTabs()
    const { toast } = useToast()
    const version = useDataVersion('users')
    const rolesVersion = useDataVersion('roles') // role labels follow RoleEditor/RolesList
    const { preferences, savePreferences, user: me, can } = useAuth()
    const canWrite = can('users', 'write')
    const canDelete = can('users', 'delete')
    const list = useServerList<UserRow>(listUsers, version, {
        sort: 'createdAt', dir: 'desc', pageSize: preferences.tablePageSize?.users ?? DEFAULT_TABLE_PAGE_SIZE,
    })
    const cols = useColumnVisibility('users')

    // Resolve role key → label for display (roles are admin-readable here).
    const [roleLabels, setRoleLabels] = useState<Record<string, string>>({})
    useEffect(() => {
        listRoleOptions().then((rs) => setRoleLabels(Object.fromEntries(rs.map((r) => [r.key, r.label])))).catch(() => {})
    }, [version, rolesVersion])

    const onPageSize = (n: number) => {
        list.setPageSize(n)
        savePreferences({ tablePageSize: { ...(preferences.tablePageSize ?? {}), users: n } })
    }

    const openNew = () => openTab({ id: 'user-new', name: T.newUserTab, component: 'user-form' })
    const openEdit = (u: UserRow) => openTab({ id: `user-${u._id}`, name: fill(T.userTab, { name: u.name }), component: 'user-form', config: { id: u._id } })

    const [toDelete, setToDelete] = useState<UserRow | null>(null)
    const [delBusy, setDelBusy] = useState(false)
    const [delError, setDelError] = useState('')
    const askDelete = (u: UserRow) => { setDelError(''); setToDelete(u) }
    const cancelDelete = () => { if (!delBusy) { setToDelete(null); setDelError('') } }
    async function confirmDelete() {
        if (!toDelete) return
        setDelBusy(true); setDelError('')
        try {
            await deleteUser(toDelete._id)
            publishChange('users')
            toast(T.movedToTrash)
            setToDelete(null)
        } catch (e: any) {
            setDelError(e?.response?.data?.error ?? T.deleteFailed)
        } finally { setDelBusy(false) }
    }

    // Mehrfachauswahl → Papierkorb (components/BulkTrash.tsx): same right as the single delete.
    const bulk = useBulkTrash<UserRow>({
        kind: 'users', enabled: canDelete, rows: list.rows, rowId: (r) => r._id, total: list.total,
        filter: { q: list.q },
        whyNot: (u) => (u._id === me?._id ? T.ownAccountNotDeletable : ''),
        onDone: () => publishChange('users'),
    })

    const columns: Column<UserRow>[] = [
        { key: 'name', header: T.columns.name, sortField: 'name', defaultWidth: 160, render: (u) => u.name },
        { key: 'email', header: T.columns.email, sortField: 'email', defaultWidth: 200, render: (u) => u.email },
        { key: 'role', header: T.columns.role, sortField: 'role', defaultWidth: 130, render: (u) => roleLabels[u.role] ?? u.role },
        { key: 'active', header: T.columns.status, sortField: 'active', className: 'center', defaultWidth: 90, render: (u) => <span className={`badge ${u.active ? 'sent' : 'void'}`}>{u.active ? T.active : T.inactive}</span> },
        { key: 'createdAt', header: T.columns.created, sortField: 'createdAt', defaultWidth: 100, render: (u) => new Date(u.createdAt).toLocaleDateString('de-DE') },
        {
            key: 'actions', header: T.columns.actions, defaultWidth: 100, sticky: true,
            render: (u) => (
                <div className="row-actions">
                    <button className="icon-action" title={u._id === me?._id || !canWrite ? T.view : T.edit} aria-label={u._id === me?._id || !canWrite ? T.view : T.edit} onClick={() => openEdit(u)}><EditIcon /></button>
                    {u._id !== me?._id && canDelete && <button className="icon-action" title={T.delete} aria-label={T.delete} onClick={() => askDelete(u)}><TrashIcon /></button>}
                </div>
            ),
        },
    ]
    if (bulk.column) columns.unshift(bulk.column)

    return (
        <div className="list-page">
            <ListToolbar title={T.title} infoText={T.info} action={canWrite ? <button onClick={openNew}>{T.newUser}</button> : undefined} />

            {list.error && <div className="notice err">{list.error}</div>}

            {bulk.bar}

            <ListPanel
                count={list.total}
                grandTotal={list.grandTotal}
                filtered={list.q.trim() !== ''}
                search={{ value: list.q, onChange: list.setQ }}
                columnManager={<ColumnManager columns={columns} hidden={cols.hidden} onToggle={cols.toggle} onReset={cols.reset} />}
            >
                {list.loading ? <p className="muted">{T.loading}</p> : list.rows.length === 0 ? (
                    <p className="muted">{T.noneFound}</p>
                ) : (
                    <DataTable key={cols.resetKey} tableId="users" columns={columns} hidden={cols.hidden}
                        rows={list.rows} rowKey={(u) => u._id} sort={list.sort} dir={list.dir} onSort={list.toggleSort}
                        onRowActivate={openEdit} />
                )}
            </ListPanel>

            <Pagination page={list.page} pageCount={list.pageCount} onPage={list.setPage} pageSize={list.pageSize} onPageSize={onPageSize} />

            <ConfirmDialog
                open={!!toDelete}
                title={T.toTrash}
                message={toDelete ? <>{fill(T.deleteQuestion, { name: toDelete.name })}<br />{T.deleteNote}</> : null}
                confirmLabel={T.toTrash}
                danger
                busy={delBusy}
                error={delError}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
            />
            {bulk.dialogs}
        </div>
    )
}
